/**
 * What a task is worth, and what a person has actually earned.
 *
 * One rule, stated once. A completed day of an exercise is worth `xpBase(min)`,
 * and that value is SPLIT across the patterns the exercise targets — by their
 * authored weights, never multiplied by their count. Three patterns do not make
 * the same fifteen minutes worth three times as much; they share it. Otherwise
 * the cheapest way to earn points would be to pick whichever exercise happens to
 * name the most patterns, which is a property of the content, not of the effort.
 *
 * Points are paid ONLY into patterns. A direction's total is the sum of its
 * patterns' shares, and the overall dynamic total is the sum over everything —
 * so per-pattern, per-direction and overall always reconcile by construction.
 *
 * Every pattern's share also credits the direction that pattern lives in, which
 * is how one task moves several directions at once: `three-good-things` names a
 * pattern in attention and one in cognition, and the points follow them there
 * rather than piling onto the exercise's own track.
 *
 * These points are NOT the measured score. The score comes from the test and
 * moves on a retake; points are what the work has added since. The two are shown
 * side by side (see `Honesty` on the State screen) rather than blended, so a
 * number that was measured never quietly becomes a number that was earned.
 */
import { AppContent, Exercise } from './content';
import { xpBase } from './tokens';
import { TakenTask, dayKey, todayIndex } from './tasks';

/** Points to the next level. One level ≈ a solid week of work. */
export const LEVEL_STEP = 300;

/** Daily target the week chart draws its dashed line at. */
export const DAY_GOAL = 50;

export interface Split {
    /** What one completed day of this exercise is worth, all in. */
    total: number;
    /** [pattern code, points] — empty when the exercise names no pattern. */
    byPattern: Array<[string, number]>;
    /** [direction code, points], largest share first. Always at least one. */
    byDirection: Array<[string, number]>;
}

/**
 * Splits `n` points into whole shares proportional to `weights`, summing to `n`.
 *
 * Largest-remainder apportionment: floor every proportional share, then hand
 * the leftover points to the largest fractional parts, earliest index on a tie.
 * Equal weights therefore reproduce the old equal split exactly — remainder to
 * the first-named pattern — and integer shares mean nothing is ever lost to
 * rounding on any completion.
 */
function shares(n: number, weights: number[]): number[] {
    const total = weights.reduce((a, b) => a + b, 0);
    const exact = weights.map((w) => (n * w) / total);
    const out = exact.map(Math.floor);
    const order = exact
        .map((v, i) => [v - Math.floor(v), i] as const)
        .sort((a, b) => b[0] - a[0] || a[1] - b[1]);
    let rest = n - out.reduce((a, b) => a + b, 0);

    for (let k = 0; rest > 0; k += 1, rest -= 1) out[order[k][1]] += 1;
    return out;
}

export function splitPoints(ex: Exercise, content: AppContent): Split {
    const total = xpBase(ex.min);
    // A pattern the payload does not carry cannot be credited; dropping it here
    // (with its weight) keeps the split summing to `total` instead of paying
    // into a dead code.
    const entries = (ex.pats || [])
        .map((code, i) => [code, ex.patW?.[i] || 1] as const)
        .filter(([code]) => content.patBy[code]);

    if (!entries.length) return { total, byPattern: [], byDirection: [[ex.track, total]] };

    const cut = shares(total, entries.map(([, w]) => w));
    const byPattern = entries.map(([code], i) => [code, cut[i]] as [string, number]);
    const byDir: Record<string, number> = {};

    byPattern.forEach(([code, pts]) => {
        const dim = content.patBy[code]?.dim || ex.track;

        byDir[dim] = (byDir[dim] || 0) + pts;
    });

    return {
        total,
        byPattern,
        byDirection: Object.entries(byDir).sort((a, b) => b[1] - a[1]),
    };
}

export interface Earned {
    /** Every point from every completed day. */
    total: number;
    byDirection: Record<string, number>;
    byPattern: Record<string, number>;
    /** Completed days, across all tasks. One task done twice counts twice. */
    reports: number;
    /** Distinct days on which anything was completed — the maturity signal. */
    daysOfHistory: number;
    /** Points per weekday of the current week, Monday first. */
    week: number[];
    /** The same week, split by direction — what a direction's trend chart draws. */
    weekByDirection: Record<string, number[]>;
    /** Consecutive days with at least one completed task, ending today or yesterday. */
    streak: number;
}

const EMPTY_EARNED: Earned = {
    total: 0,
    byDirection: {},
    byPattern: {},
    reports: 0,
    daysOfHistory: 0,
    week: [0, 0, 0, 0, 0, 0, 0],
    weekByDirection: {},
    streak: 0,
};

/**
 * Rolls the task log up into everything the screens display.
 *
 * Every day's worth comes from the take's FROZEN `price_snapshot` (see
 * `tasks.ts`), never recomputed from current content: retuning a weight or
 * rewriting an exercise later must not rewrite what was already earned.
 * Recomputed as a walk over the entries rather than kept as a running total,
 * so un-marking a day simply takes its points back.
 */
export function earnedFrom(tasks: TakenTask[]): Earned {
    if (!tasks.length) return EMPTY_EARNED;

    const byDirection: Record<string, number> = {};
    const byPattern: Record<string, number> = {};
    const perDay: Record<string, number> = {};
    const perDayByDirection: Record<string, Record<string, number>> = {};
    let total = 0;
    let reports = 0;

    tasks.forEach((task) => {
        Object.entries(task.log || {}).forEach(([day, entry]) => {
            if (!entry?.done) return;
            // A day that cannot name its step has no price — paying a guessed
            // amount would desynchronise the ledgers this file promises align.
            const price = entry.sub ? task.steps?.[entry.sub] : undefined;

            if (!price) return;
            reports += 1;
            total += price.total;
            perDay[day] = (perDay[day] || 0) + price.total;
            Object.entries(price.by_direction || {}).forEach(([code, pts]) => {
                byDirection[code] = (byDirection[code] || 0) + pts;
                perDayByDirection[code] = perDayByDirection[code] || {};
                const dirDays = perDayByDirection[code];

                dirDays[day] = (dirDays[day] || 0) + pts;
            });
            Object.entries(price.by_pattern || {}).forEach(([code, pts]) => {
                byPattern[code] = (byPattern[code] || 0) + pts;
            });
        });
    });

    // Monday of the current week, then seven days forward — the same window the
    // per-task week dots use, so the two never disagree about which day is which.
    const week: number[] = [];
    const cursor = new Date();

    cursor.setDate(cursor.getDate() - todayIndex());
    const weekByDirection: Record<string, number[]> = {};

    Object.keys(perDayByDirection).forEach((code) => {
        weekByDirection[code] = [];
    });
    for (let i = 0; i < 7; i += 1) {
        const key = dayKey(cursor);

        week.push(perDay[key] || 0);
        Object.entries(weekByDirection).forEach(([code, days]) => {
            days.push(perDayByDirection[code][key] || 0);
        });
        cursor.setDate(cursor.getDate() + 1);
    }

    // Today not being logged yet must not zero a live streak, so the walk starts
    // at today and allows only the first day to be missing.
    let streak = 0;
    const back = new Date();

    for (let i = 0; i < 400; i += 1) {
        if (perDay[dayKey(back)]) streak += 1;
        else if (i > 0) break;
        back.setDate(back.getDate() - 1);
    }

    return {
        total,
        byDirection,
        byPattern,
        reports,
        daysOfHistory: Object.keys(perDay).length,
        week,
        weekByDirection,
        streak,
    };
}

export interface LevelState {
    level: number;
    /** Points still needed for the next level. */
    toNext: number;
    /** 0–1 progress through the current level, for the bar. */
    pct: number;
}

export function levelOf(points: number): LevelState {
    const into = points % LEVEL_STEP;

    return {
        level: Math.floor(points / LEVEL_STEP) + 1,
        toNext: LEVEL_STEP - into,
        pct: into / LEVEL_STEP,
    };
}
